import { useState, useEffect } from 'react';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { supabase } from '@/integrations/supabase/client';
import { 
  Ticket, 
  Plus, 
  Minus, 
  Lock, 
  Check, 
  CreditCard,
  Gift,
  Sparkles,
  Trophy,
  Users
} from 'lucide-react';

interface PassPack {
  id: string;
  name: string;
  passes: number;
  price: number;
  originalPrice?: number;
  savePercent?: number;
  popular?: boolean;
  bonus?: string;
}

const passPacks: PassPack[] = [
  {
    id: 'starter',
    name: 'Starter',
    passes: 1,
    price: 4.99,
  },
  {
    id: 'competitor',
    name: 'Competitor',
    passes: 5,
    price: 22.49,
    originalPrice: 24.95,
    savePercent: 10,
  },
  {
    id: 'challenger',
    name: 'Challenger',
    passes: 10,
    price: 39.99,
    originalPrice: 49.90,
    savePercent: 20,
    popular: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    passes: 25,
    price: 89.99,
    originalPrice: 124.75,
    savePercent: 28,
    bonus: '+5 Bonus Passes',
  },
];

export default function BuyFieldPass() {
  const [selectedPack, setSelectedPack] = useState<PassPack | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [promoCode, setPromoCode] = useState('');
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promoApplied, setPromoApplied] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userProfile, refetch: refetchProfile } = useUserProfile();
  const [paymentForm, setPaymentForm] = useState({
    email: 'user@draftactics.com',
    name: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    agreeTerms: false,
  });
  const { toast } = useToast();

  const currentPasses = userProfile?.field_passes || 0;
  const promoDiscount = promoApplied ? 0.1 : 0; // 10% discount
  const subtotal = selectedPack ? selectedPack.price * quantity : 0;
  const discount = subtotal * promoDiscount;
  const total = subtotal - discount;

  const handlePackSelect = (pack: PassPack) => {
    setSelectedPack(pack);
    setQuantity(1);
  };

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
  };

  const handlePromoApply = () => {
    if (promoCode.toLowerCase() === 'welcome10') {
      setPromoApplied(true);
      toast({
        title: "Code applied!",
        description: "10% discount has been applied to your order.",
      });
    } else {
      toast({
        title: "Invalid code",
        description: "Please check your promo code and try again.",
        variant: "destructive",
      });
    }
  };

  const handleCheckout = async () => {
    if (!selectedPack) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          packId: selectedPack.id,
          quantity: quantity,
        },
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe checkout in new tab
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Checkout failed",
        description: "Unable to start checkout process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    // This function is now unused as we redirect to Stripe
    handleCheckout();
  };

  const totalPasses = selectedPack ? selectedPack.passes * quantity + (selectedPack.bonus ? 5 : 0) : 0;

  // Handle URL parameters for success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    
    if (success === '1') {
      setShowSuccess(true);
      // Refetch user profile to update field passes
      refetchProfile();
      toast({
        title: "Payment successful!",
        description: "Your Field Passes have been added to your account.",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, '/buy-field-pass');
    }
    
    if (canceled === '1') {
      toast({
        title: "Payment canceled",
        description: "Your purchase was canceled. No charges were made.",
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, '/buy-field-pass');
    }
  }, [refetchProfile, toast]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col">
          <AppHeader />
          <main className="flex-1 p-6 bg-background">
            <div className="max-w-7xl mx-auto">
              {/* Header Section */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-foreground mb-2">Buy Field Passes</h1>
                <p className="text-muted-foreground">
                  Enter tournaments, level up faster, unlock more rewards.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-3 space-y-8">
                  {/* Current Balance Notice */}
                  {currentPasses > 0 && (
                    <Card className="border-primary/20 bg-primary/5">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Ticket className="h-4 w-4 text-primary" />
                          <span>You currently have <strong>{currentPasses}</strong> Field Passes</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pass Packs Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {passPacks.map((pack) => (
                      <Card
                        key={pack.id}
                        className={`relative cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${
                          selectedPack?.id === pack.id
                            ? 'border-primary shadow-premium ring-2 ring-primary/50'
                            : 'hover:border-primary/50'
                        }`}
                        onClick={() => handlePackSelect(pack)}
                      >
                        {pack.popular && (
                          <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-primary">
                            Most Popular
                          </Badge>
                        )}
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center justify-between">
                            <span>{pack.name}</span>
                            {pack.bonus && <Sparkles className="h-4 w-4 text-warning" />}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold">€{pack.price}</div>
                            {pack.originalPrice && (
                              <div className="text-sm text-muted-foreground line-through">
                                €{pack.originalPrice}
                              </div>
                            )}
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Ticket className="h-4 w-4 text-primary" />
                              <span>{pack.passes} Field Passes</span>
                            </div>
                            {pack.bonus && (
                              <div className="flex items-center gap-2">
                                <Gift className="h-4 w-4 text-warning" />
                                <span className="text-warning">{pack.bonus}</span>
                              </div>
                            )}
                            {pack.savePercent && (
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-xs">
                                  Save {pack.savePercent}%
                                </Badge>
                              </div>
                            )}
                          </div>

                          <div className="text-xs text-muted-foreground text-center">
                            €{(pack.price / pack.passes).toFixed(2)} per pass
                          </div>

                          <Button
                            variant={selectedPack?.id === pack.id ? "default" : "outline"}
                            className="w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePackSelect(pack);
                            }}
                          >
                            {selectedPack?.id === pack.id ? 'Selected' : 'Select'}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Quantity Selector */}
                  {selectedPack && (
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Quantity</span>
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleQuantityChange(-1)}
                              disabled={quantity <= 1}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">{quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleQuantityChange(1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* FAQ Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Frequently Asked Questions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="what-are">
                          <AccordionTrigger>What are Field Passes?</AccordionTrigger>
                          <AccordionContent>
                            Field Passes are your entry tickets to tournaments and competitions. Each tournament requires one Field Pass to join, and they help you earn XP faster and unlock exclusive rewards.
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="expire">
                          <AccordionTrigger>Do Field Passes expire?</AccordionTrigger>
                          <AccordionContent>
                            No, Field Passes never expire. Once purchased, they remain in your account until you use them to enter tournaments.
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="refund">
                          <AccordionTrigger>Can I refund a purchase?</AccordionTrigger>
                          <AccordionContent>
                            Unused Field Passes can be refunded within 30 days of purchase. Contact our support team for assistance with refunds.
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="balance">
                          <AccordionTrigger>Where can I see my balance?</AccordionTrigger>
                          <AccordionContent>
                            Your Field Pass balance is always visible in the top header of the app, and you can view detailed history in your Profile page.
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </CardContent>
                  </Card>
                </div>

                {/* Order Summary Sidebar */}
                <div className="lg:col-span-1">
                  <Card className="sticky top-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Order Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedPack ? (
                        <>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{selectedPack.name} Pack</span>
                              <span>€{selectedPack.price}</span>
                            </div>
                            {quantity > 1 && (
                              <div className="flex justify-between text-sm text-muted-foreground">
                                <span>Quantity: {quantity}</span>
                                <span>€{subtotal.toFixed(2)}</span>
                              </div>
                            )}
                            {promoApplied && (
                              <div className="flex justify-between text-sm text-success">
                                <span>Promo Discount</span>
                                <span>-€{discount.toFixed(2)}</span>
                              </div>
                            )}
                          </div>

                          <Separator />

                          <div className="flex justify-between font-bold text-lg">
                            <span>Total</span>
                            <span className="font-mono">€{total.toFixed(2)}</span>
                          </div>

                          <div className="text-sm text-muted-foreground">
                            You'll receive <strong>{totalPasses}</strong> Field Passes
                          </div>

                          {/* Promo Code */}
                          <div className="space-y-2">
                            <Button
                              variant="ghost"
                              className="w-full justify-start p-0 h-auto text-sm"
                              onClick={() => setPromoExpanded(!promoExpanded)}
                            >
                              Have a promo code?
                            </Button>
                            {promoExpanded && (
                              <div className="space-y-2 animate-fade-in">
                                <Input
                                  placeholder="Enter code"
                                  value={promoCode}
                                  onChange={(e) => setPromoCode(e.target.value)}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handlePromoApply}
                                  className="w-full"
                                >
                                  Apply
                                </Button>
                              </div>
                            )}
                          </div>

                          <Button
                            onClick={handleCheckout}
                            className="w-full"
                            size="lg"
                            disabled={loading}
                          >
                            {loading ? "Starting checkout..." : "Checkout with Stripe"}
                          </Button>

                          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                            <Lock className="h-3 w-3" />
                            <span>Secure checkout</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-center text-muted-foreground py-8">
                          <Ticket className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>Select a pack to continue</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Your Purchase</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={paymentForm.email}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name on card</Label>
                <Input
                  id="name"
                  value={paymentForm.name}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cardNumber">Card number</Label>
              <Input
                id="cardNumber"
                placeholder="1234 5678 9012 3456"
                value={paymentForm.cardNumber}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, cardNumber: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiry">Expiry</Label>
                <Input
                  id="expiry"
                  placeholder="MM/YY"
                  value={paymentForm.expiry}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, expiry: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvc">CVC</Label>
                <Input
                  id="cvc"
                  placeholder="123"
                  value={paymentForm.cvc}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, cvc: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={paymentForm.agreeTerms}
                onCheckedChange={(checked) => setPaymentForm(prev => ({ ...prev, agreeTerms: checked as boolean }))}
              />
              <Label htmlFor="terms" className="text-sm">
                I agree to the Terms and Privacy Policy
              </Label>
            </div>

            <Separator />

            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="font-mono">€{total.toFixed(2)}</span>
            </div>

            <Button
              onClick={handlePayment}
              disabled={loading || !paymentForm.agreeTerms}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Processing...
                </>
              ) : (
                'Pay Now'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md text-center">
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
              <Check className="h-8 w-8 text-success" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Purchase Successful!</h3>
              <p className="text-muted-foreground">
                You now have <strong>{currentPasses + totalPasses}</strong> Field Passes.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSuccess(false);
                  window.location.href = '/';
                }}
                className="flex-1"
              >
                <Trophy className="h-4 w-4 mr-2" />
                Go to Rewards Hub
              </Button>
              <Button
                onClick={() => {
                  setShowSuccess(false);
                  window.location.href = '/leagues';
                }}
                className="flex-1"
              >
                <Users className="h-4 w-4 mr-2" />
                Back to Leagues
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}